import { useState } from "react";
import { X, Heart, Bookmark, BookmarkCheck, ExternalLink, MapPin, Clock, ChevronLeft, ChevronRight, Volume2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CommentsSection from "@/components/CommentsSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_COLORS = {
  "Landmarks": "#2D6A4F",
  "Hidden Gems": "#B85C38",
  "Vanished Voices": "#5C4B8A",
  "Wild Echoes": "#1A5276",
  "City Pulses": "#8B5E3C",
};

export default function EchoeDetailModal({ story, onClose, onFavorite, canFavorite, onBookmark, isBookmarked, canBookmark }) {
  const { user } = useAuth();
  const [carouselIndex, setCarouselIndex] = useState(0);

  if (!story) return null;

  const catColor = CATEGORY_COLORS[story.category] || "#718096";
  const media = story.media || [];
  const images = media.filter(m => m.media_type === "image");
  const audios = media.filter(m => m.media_type === "audio");

  const nextImage = () => setCarouselIndex(i => (i + 1) % images.length);
  const prevImage = () => setCarouselIndex(i => (i - 1 + images.length) % images.length);

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="echoe-detail-modal">
      <div className="modal-content max-w-3xl" onClick={e => e.stopPropagation()} data-testid="echoe-detail-content">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#E5E2D9]">
          <div className="flex items-center gap-3">
            <span className="type-badge" style={{ borderColor: catColor, color: catColor }}>
              {story.category}
            </span>
            {story.na_id && (
              <span className="type-badge">
                <MapPin size={10} /> Sourced
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canBookmark && (
              <button
                className={`btn-ghost ${isBookmarked ? "text-[#B85C38]" : ""}`}
                onClick={() => onBookmark?.(story)}
                data-testid="echoe-detail-bookmark"
              >
                {isBookmarked ? <BookmarkCheck size={16} fill="#B85C38" /> : <Bookmark size={16} />}
                <span className="text-xs">{isBookmarked ? "Saved" : "Save"}</span>
              </button>
            )}
            <button className="btn-ghost p-2" onClick={onClose} data-testid="echoe-detail-close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Title & Meta */}
        <div className="p-6 border-b border-[#E5E2D9] bg-[#F9F7F1]">
          <h2 className="font-serif text-2xl sm:text-3xl font-medium text-[#1A1A1A] leading-tight mb-3" data-testid="echoe-detail-title">
            {story.title}
          </h2>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm text-[#4A5568] font-medium">
              by {story.author_name}
            </span>
            <span className="font-mono text-xs text-[#718096]">
              <Clock size={11} className="inline mr-1" />
              {new Date(story.timestamp).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </span>
            {story.location_coordinates && (() => {
              const loc = story.location_coordinates;
              // location_coordinates is normalised to {lat,lng,address?,place_name?}
              // by the backend; gracefully handle legacy string shape too.
              const label = typeof loc === "string"
                ? loc
                : (loc.place_name || loc.address || (loc.lat != null && loc.lng != null ? `${loc.lat.toFixed(3)}, ${loc.lng.toFixed(3)}` : null));
              if (!label) return null;
              return (
                <span className="font-mono text-xs text-[#718096]">
                  <MapPin size={11} className="inline mr-1" />
                  {label}
                </span>
              );
            })()}
          </div>
        </div>

        {/* Image Carousel */}
        {images.length > 0 && (
          <div className="relative border-b border-[#E5E2D9] bg-[#1A1A1A]" data-testid="echoe-carousel">
            <img
              src={`${BACKEND}${images[carouselIndex].url}`}
              alt={`Photo ${carouselIndex + 1}`}
              className="w-full max-h-96 object-contain mx-auto"
              data-testid="echoe-carousel-image"
            />
            {images.length > 1 && (
              <>
                <button
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  onClick={prevImage}
                  data-testid="carousel-prev"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  onClick={nextImage}
                  data-testid="carousel-next"
                >
                  <ChevronRight size={16} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((img, i) => (
                    <button
                      key={img.url || `img-${i}`}
                      className={`w-2 h-2 rounded-full transition-all ${i === carouselIndex ? "bg-white w-4" : "bg-white/50"}`}
                      onClick={() => setCarouselIndex(i)}
                      data-testid={`carousel-dot-${i}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Legacy image_url fallback (for older stories without media array) */}
        {images.length === 0 && story.original_image_url && (
          <div className="border-b border-[#E5E2D9]">
            <img
              src={story.original_image_url}
              alt={story.title}
              className="w-full max-h-80 object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        )}

        {/* Full Content */}
        <div className="p-6">
          <p className="text-base text-[#4A5568] leading-relaxed whitespace-pre-wrap" data-testid="echoe-detail-content-text">
            {story.content}
          </p>
        </div>

        {/* Audio Players */}
        {audios.length > 0 && (
          <div className="px-6 pb-4" data-testid="echoe-audio-section">
            <h4 className="font-mono text-xs uppercase tracking-widest text-[#718096] mb-3">
              Audio ({audios.length})
            </h4>
            <div className="space-y-2">
              {audios.map((a) => (
                <div key={a.url || a.original_name} className="border border-[#E5E2D9] p-3 bg-[#F9F7F1] flex items-center gap-3">
                  <Volume2 size={16} className="text-[#718096] flex-shrink-0" />
                  <audio controls className="flex-1 h-8" src={`${BACKEND}${a.url}`}>
                    Your browser does not support audio.
                  </audio>
                  <span className="font-mono text-[10px] text-[#718096]">{a.original_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Source Link */}
        {story.na_id && (
          <div className="px-6 pb-4">
            <a
              href={story.na_id.startsWith("http") ? story.na_id : `https://catalog.archives.gov/id/${story.na_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1A2B3C] text-white font-mono text-xs hover:bg-[#2A405A] transition-colors"
              data-testid="echoe-detail-source-link"
            >
              <ExternalLink size={13} />
              View on National Archives
            </a>
          </div>
        )}

        {/* Comments */}
        <div className="px-6 pb-6">
          <CommentsSection storyId={story.id} />
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between p-5 border-t border-[#E5E2D9] bg-[#F9F7F1]">
          <span className="font-mono text-xs text-[#718096] uppercase tracking-widest">
            {story.author_role === "echo_maker" ? "Echoe Maker" : "Echoe Hunter"}
          </span>
          <button
            className={`btn-ghost flex items-center gap-1.5 ${story.is_favorited ? "text-[#B85C38]" : ""}`}
            onClick={() => canFavorite && onFavorite?.(story.id)}
            disabled={!canFavorite}
            data-testid="echoe-detail-favorite"
          >
            <Heart size={18} fill={story.is_favorited ? "#B85C38" : "none"} />
            <span className="text-sm font-mono">{story.favorites_count || 0}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
